import { CONVERSATION_TYPES } from './conversation-type-selection.gjs';
import { MODEL_NAMES } from '../../constants/index';
import { TrackedSet } from 'tracked-built-ins';
import { cached, tracked } from '@glimmer/tracking';
import { didCancel, task, timeout } from 'ember-concurrency';
import {
  pageNumberInfinityModel,
  pageNumberInfinityOptions,
} from '../../utils/messaging/infinity-model';
import { service } from '@ember/service';
import MessagingRoutesBaseHandler from './base-handler.js';

export const CONTACT_PAGE_SIZE = 15;
export const CONTACT_ROLE_FILTER = {
  role: ['primary clinician', 'couple', 'primary clinician for contact'],
};

export default class MessagingRoutesContactsHandler extends MessagingRoutesBaseHandler {
  @service alertManager;
  @service infinity;
  @service intl;
  @service messaging;
  @service messagingNavigation;

  @tracked search = '';
  @tracked isRecentContacts = true;
  @tracked contacts = [];

  selectedContactIds = new TrackedSet();

  /** Map of contact id → contact model. Persists across search so we can resolve
  selected ids to contacts and reuse references. */
  #contactsCache = new Map();

  constructor(owner, options) {
    super(owner, options);
    this.conversationType = options.conversationType;
  }

  get isGroup() {
    return this.conversationType === CONVERSATION_TYPES.GROUP;
  }

  get shouldFilter() {
    return Boolean(this.search?.trim()) || this.isGroup;
  }

  @cached
  get selectableContacts() {
    const contacts = this.contacts;
    if (!contacts || !contacts.length) return [];

    return contacts.content.map(contact => this.#getOrCreateCachedContact(contact));
  }

  get selectedContacts() {
    return Array.from(this.selectedContactIds)
      .map(id => this.#contactsCache.get(id))
      .filter(Boolean);
  }

  onVisit = task({ drop: true }, async () => {
    await this.searchContacts.perform();
  });

  searchContacts = task({ restartable: true }, async () => {
    const searchValue = this.search?.trim();
    const filter = this.shouldFilter
      ? {
          ...(searchValue && { search: searchValue }),
          ...(this.isGroup && { 'external_scope': 'User' }),
        }
      : undefined;

    // Add debounce only for search input (not initial load)
    if (this.search) {
      await timeout(300);
    }

    try {
      await this.getContacts.perform(filter);
      this.isRecentContacts = !this.shouldFilter;
    } catch (err) {
      this.handleError(err);
    }
  });

  getContacts = task({ drop: true }, async (filter = null) => {
    const model = await this.infinity.model(
      MODEL_NAMES.CONTACT,
      {
        ...(filter && { filter }),
        include: 'couple',
        ...(this.shouldFilter && { sort: 'name' }),
        ...pageNumberInfinityOptions(CONTACT_PAGE_SIZE),
      },
      pageNumberInfinityModel(CONTACT_PAGE_SIZE)
    );

    this.contacts = model;
  });

  updateSearch(search) {
    this.search = search;
    this.searchContacts.perform();
  }

  loadMoreContacts = task({ drop: true }, async model => {
    if (!model.canLoadMore) return;
    try {
      await this.infinity.loadNextPage(model);
    } catch (err) {
      this.handleError(err);
    }
  });

  handleError(err) {
    if (didCancel(err)) return;
    this.alertManager.broadcast('toast', {
      message: this.intl.t('emc.general_error'),
      type: 'error',
    });
  }

  selectContact = contact => {
    if (this.isGroup) {
      this.toggleContactSelection(contact);
    } else {
      const conversation = this.messaging.findOrCreateConversationWithContacts(contact.members);
      this.messagingNavigation.toConversation(conversation);
    }
  };

  toggleContactSelection = contact => {
    const id = contact.id;
    if (this.selectedContactIds.has(id)) {
      this.selectedContactIds.delete(id);
    } else {
      this.selectedContactIds.add(id);
    }
  };

  isContactSelected(id) {
    return this.selectedContactIds.has(id);
  }

  #getOrCreateCachedContact(contact) {
    this.#contactsCache.set(contact.id, contact);
    return contact;
  }
}
